import { Extension, type Editor, type Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import Suggestion, { type SuggestionKeyDownProps, type SuggestionProps } from "@tiptap/suggestion";
import {
  AtSign, CalendarDays, Hash, CheckSquare, ChevronRightSquare, CirclePlus, Clock, Code, FilePlus2, FileText, Heading1, Heading2, Heading3, Heading4, Image as ImageIcon,
  Lightbulb, Link, Link2, List, ListOrdered, ListTree, Minus, Paperclip, Plus, Quote, Mic, PenLine, SeparatorHorizontal, Sigma, SquareCode, Table, Type, Workflow,
} from "lucide-react";
import { useEffect, useImperativeHandle, useState, type ReactNode, type Ref } from "react";
import { addTagToNotes, createNote, createTag } from "../../db/actions";
import { db } from "../../db/client";
import { todayIso } from "./blocks";
import { newSketch } from "./sketch";
import { recordAudio } from "./recorder";
import { insertNewTask } from "./tasks";
import { dateFmt } from "../format";

export interface EditorEnv {
  noteId: string;
  /** Opens the file picker; `accept` narrows it (e.g. "image/*"). */
  pickFiles: (accept?: string) => void;
}

export interface Command {
  title: string;
  group: string;
  keywords?: string;
  icon: ReactNode;
  hint?: string;
  run: (editor: Editor, range: Range, env: EditorEnv) => void;
}

export function formatDate(d = new Date()): string {
  return dateFmt({ year: "numeric", month: "short", day: "numeric" }).format(d);
}

export function formatTime(d = new Date()): string {
  return dateFmt({ hour: "numeric", minute: "2-digit" }).format(d);
}

/** A new note in the same notebook, linked from here (Evernote's "New linked note"). */
async function newLinkedNote(e: Editor, r: Range, env: EditorEnv) {
  const here = await db.get<{ notebook_id: string }>("SELECT notebook_id FROM notes WHERE id = ?", [env.noteId]);
  const id = await createNote(here ? { notebookId: here.notebook_id } : {});
  e.chain()
    .focus()
    .deleteRange(r)
    .insertContent([
      { type: "text", text: "Untitled note", marks: [{ type: "link", attrs: { href: `/note/${id}`, "data-note-link": id } }] },
      { type: "text", text: " " },
    ])
    .run();
}

/** Evernote's Insert menu, in its groups and order (also what "/" shows). */
export const COMMANDS: Command[] = [
  { group: "Essentials", title: "Page break", keywords: "print new page", icon: <SeparatorHorizontal size={16} />, run: (e, r) => e.chain().focus().deleteRange(r).insertContent([{ type: "pageBreak" }, { type: "paragraph" }]).run() },
  { group: "Essentials", title: "New task", keywords: "todo reminder task", icon: <CirclePlus size={16} className="ic-task" />, hint: "Alt+T", run: (e, r, env) => insertNewTask(e, env.noteId, r) },
  { group: "Essentials", title: "New linked note", keywords: "note link create", icon: <FilePlus2 size={16} className="ic-create" />, run: (e, r, env) => void newLinkedNote(e, r, env) },
  { group: "Essentials", title: "Inline tag", keywords: "tag hashtag #", icon: <Hash size={16} />, hint: "#", run: (e, r) => { const $r = e.state.doc.resolve(r.from); const prev = $r.parent.textBetween(Math.max(0, $r.parentOffset - 1), $r.parentOffset); e.chain().focus().deleteRange(r).insertContent(prev && prev !== " " ? " #" : "#").run(); } },
  { group: "Essentials", title: "Link to note", keywords: "note link mention @", icon: <Link2 size={16} />, hint: "@", run: (e, r) => e.chain().focus().deleteRange(r).insertContent("@").run() },
  {
    group: "Essentials",
    title: "Mention a date",
    keywords: "date day calendar @",
    icon: <AtSign size={16} />,
    // The chip is selected after insert, which opens its date picker (Evernote does the same).
    run: (e, r) => e.chain().focus().deleteRange(r).insertContent([{ type: "dateMention", attrs: { date: todayIso() } }, { type: "text", text: " " }]).setNodeSelection(r.from).run(),
  },
  { group: "Essentials", title: "Link", keywords: "url web href", icon: <Link size={16} />, hint: "Ctrl+K", run: (e, r) => (e.chain().focus().deleteRange(r).run(), window.dispatchEvent(new Event("engram:link"))) },
  { group: "Essentials", title: "Table of contents", keywords: "toc headings outline", icon: <ListTree size={16} />, run: (e, r) => e.chain().focus().deleteRange(r).insertContent([{ type: "toc" }, { type: "paragraph" }]).run() },
  { group: "Essentials", title: "Table", keywords: "grid", icon: <Table size={16} />, run: (e, r) => e.chain().focus().deleteRange(r).insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run() },
  { group: "Essentials", title: "Divider", keywords: "hr line rule", icon: <Minus size={16} />, hint: "---", run: (e, r) => e.chain().focus().deleteRange(r).setHorizontalRule().run() },
  { group: "Essentials", title: "Quote", keywords: "blockquote", icon: <Quote size={16} />, hint: ">", run: (e, r) => e.chain().focus().deleteRange(r).toggleBlockquote().run() },
  { group: "Essentials", title: "Callout", keywords: "note info warning box emoji", icon: <Lightbulb size={16} />, run: (e, r) => e.chain().focus().deleteRange(r).wrapIn("callout").run() },
  { group: "Essentials", title: "Toggle", keywords: "collapse fold details expand", icon: <ChevronRightSquare size={16} />, run: (e, r) => e.chain().focus().deleteRange(r).setDetails().updateAttributes("details", { open: true }).run() },
  { group: "Text Styles", title: "Large heading", keywords: "h1 header title", icon: <Heading1 size={16} />, hint: "#", run: (e, r) => e.chain().focus().deleteRange(r).setHeading({ level: 1 }).run() },
  { group: "Text Styles", title: "Medium heading", keywords: "h2 header", icon: <Heading2 size={16} />, hint: "##", run: (e, r) => e.chain().focus().deleteRange(r).setHeading({ level: 2 }).run() },
  { group: "Text Styles", title: "Small heading", keywords: "h3 header", icon: <Heading3 size={16} />, hint: "###", run: (e, r) => e.chain().focus().deleteRange(r).setHeading({ level: 3 }).run() },
  { group: "Text Styles", title: "Extra small heading", keywords: "h4 header", icon: <Heading4 size={16} />, hint: "####", run: (e, r) => e.chain().focus().deleteRange(r).setHeading({ level: 4 }).run() },
  { group: "Text Styles", title: "Normal text", keywords: "paragraph body", icon: <Type size={16} />, run: (e, r) => e.chain().focus().deleteRange(r).setParagraph().run() },
  { group: "Lists", title: "Bullet list", keywords: "ul unordered bulleted", icon: <List size={16} />, hint: "*", run: (e, r) => e.chain().focus().deleteRange(r).toggleBulletList().run() },
  { group: "Lists", title: "Checklist", keywords: "todo checkbox", icon: <CheckSquare size={16} />, hint: "[ ]", run: (e, r) => e.chain().focus().deleteRange(r).toggleTaskList().run() },
  { group: "Lists", title: "Numbered list", keywords: "ol ordered", icon: <ListOrdered size={16} />, hint: "1.", run: (e, r) => e.chain().focus().deleteRange(r).toggleOrderedList().run() },
  { group: "Media", title: "Image", keywords: "picture photo upload", icon: <ImageIcon size={16} />, run: (e, r, env) => (e.chain().focus().deleteRange(r).run(), env.pickFiles("image/*")) },
  { group: "Media", title: "Audio recording", keywords: "voice record microphone memo sound", icon: <Mic size={16} />, run: (e, r, env) => (e.chain().focus().deleteRange(r).run(), recordAudio(e, env.noteId)) },
  { group: "Media", title: "Sketch", keywords: "draw drawing pen handwriting ink", icon: <PenLine size={16} />, run: (e, r, env) => (e.chain().focus().deleteRange(r).run(), newSketch(e, env.noteId)) },
  { group: "Media", title: "File", keywords: "attachment upload pdf", icon: <Paperclip size={16} />, run: (e, r, env) => (e.chain().focus().deleteRange(r).run(), env.pickFiles()) },
  { group: "Advanced", title: "Code block", keywords: "pre snippet", icon: <SquareCode size={16} />, hint: "```", run: (e, r) => e.chain().focus().deleteRange(r).toggleCodeBlock().run() },
  { group: "Advanced", title: "Formula", keywords: "math latex equation katex tex", icon: <Sigma size={16} />, run: (e, r) => e.chain().focus().deleteRange(r).insertContent([{ type: "mathBlock" }, { type: "paragraph" }]).run() },
  { group: "Advanced", title: "Diagram", keywords: "mermaid flowchart chart graph sequence", icon: <Workflow size={16} />, run: (e, r) => e.chain().focus().deleteRange(r).insertContent([{ type: "mermaidBlock" }, { type: "paragraph" }]).run() },
  { group: "Advanced", title: "Inline code", keywords: "code monospace", icon: <Code size={16} />, hint: "`", run: (e, r) => e.chain().focus().deleteRange(r).toggleCode().run() },
  { group: "Utilities", title: "Current date", keywords: "today date", icon: <CalendarDays size={16} />, hint: "Alt+Shift+D", run: (e, r) => e.chain().focus().deleteRange(r).insertContent(formatDate()).run() },
  { group: "Utilities", title: "Current time", keywords: "now clock time", icon: <Clock size={16} />, hint: "Ctrl+Shift+D", run: (e, r) => e.chain().focus().deleteRange(r).insertContent(formatTime()).run() },
];

export function filterCommands(query: string): Command[] {
  const q = query.toLowerCase().trim();
  if (!q) return COMMANDS;
  // An exact title first ("/table" + Enter is a Table, not "Table of contents"), then titles that start with the
  // query (shorter first), then titles with a word starting with it, titles containing it, keyword matches.
  const rank = (c: Command) => {
    const t = c.title.toLowerCase();
    return t === q ? 0 : t.startsWith(q) ? 1 : t.split(" ").some((w) => w.startsWith(q)) ? 2 : t.includes(q) ? 3 : c.keywords?.includes(q) ? 4 : 9;
  };
  return COMMANDS.map((c) => [rank(c), c] as const)
    .filter(([r]) => r < 9)
    .sort((x, y) => x[0] - y[0] || (x[0] === 1 ? x[1].title.length - y[1].title.length : 0))
    .map(([, c]) => c);
}

interface ListHandle {
  onKeyDown: (e: KeyboardEvent) => boolean;
}

interface Item {
  key: string;
  group?: string;
  title: string;
  icon: ReactNode;
  hint?: string;
  pick: () => void;
}

function ItemList({ items, ref, empty }: { items: Item[]; ref?: Ref<ListHandle>; empty: string }) {
  const [i, setI] = useState(0);
  useEffect(() => setI(0), [items]);
  useImperativeHandle(ref, () => ({
    onKeyDown(e) {
      if (e.key === "ArrowDown") {
        setI((x) => (x + 1) % Math.max(1, items.length));
        return true;
      }
      if (e.key === "ArrowUp") {
        setI((x) => (x - 1 + items.length) % Math.max(1, items.length));
        return true;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        items[i]?.pick();
        return !!items[i];
      }
      return false;
    },
  }));
  useEffect(() => {
    document.querySelector(".suggest-popup .menu-item.active")?.scrollIntoView({ block: "nearest" });
  }, [i]);
  return (
    <div className="menu suggest-menu">
      {items.length === 0 && <div className="menu-empty">{empty}</div>}
      {items.map((it, k) => [
        it.group && it.group !== items[k - 1]?.group ? <div key={`g:${it.group}`} className="menu-heading">{it.group}</div> : null,
        <button key={it.key} className={`menu-item${k === i ? " active" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={() => it.pick()} onMouseEnter={() => setI(k)}>
          <span className="menu-icon">{it.icon}</span>
          <span className="menu-label">{it.title}</span>
          {it.hint && <span className="menu-hint">{it.hint}</span>}
        </button>,
      ])}
    </div>
  );
}

/** Renders a suggestion list in a floating popup next to the caret. */
function popup<T>(toItems: (props: SuggestionProps<T>) => Item[], empty: string) {
  return () => {
    let renderer: ReactRenderer<ListHandle, { items: Item[]; empty: string }> | null = null;
    let el: HTMLDivElement | null = null;
    const place = (props: SuggestionProps<T>) => {
      const rect = props.clientRect?.();
      if (!rect || !el) return;
      const h = el.offsetHeight;
      const below = rect.bottom + 6 + h < window.innerHeight;
      el.style.left = `${Math.min(rect.left, window.innerWidth - el.offsetWidth - 8)}px`;
      el.style.top = `${below ? rect.bottom + 6 : rect.top - h - 6}px`;
    };
    return {
      onStart(props: SuggestionProps<T>) {
        renderer = new ReactRenderer(ItemList, { props: { items: toItems(props), empty }, editor: props.editor });
        el = document.createElement("div");
        el.className = "suggest-popup";
        el.appendChild(renderer.element);
        document.body.appendChild(el);
        requestAnimationFrame(() => place(props));
      },
      onUpdate(props: SuggestionProps<T>) {
        renderer?.updateProps({ items: toItems(props), empty });
        requestAnimationFrame(() => place(props));
      },
      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event.key === "Escape") {
          el?.remove();
          return true;
        }
        return renderer?.ref?.onKeyDown(props.event) ?? false;
      },
      onExit() {
        el?.remove();
        renderer?.destroy();
        el = null;
        renderer = null;
      },
    };
  };
}

export const SlashCommand = Extension.create<{ env: EditorEnv }>({
  name: "slashCommand",
  addOptions() {
    return { env: { noteId: "", pickFiles: () => undefined } };
  },
  addProseMirrorPlugins() {
    const env = this.options.env;
    return [
      Suggestion<Command>({
        editor: this.editor,
        pluginKey: new PluginKey("slash"),
        char: "/",
        // Evernote keeps filtering across spaces ("/table of"); a spaced query that matches nothing closes it.
        allowSpaces: true,
        startOfLine: false,
        allow: ({ state, range }) => {
          const q = state.doc.textBetween(range.from + 1, range.to, " ");
          // "yes / no", "1 / 2": a slash followed by a space is text, not a command.
          if (q.startsWith(" ")) return false;
          return !q.includes(" ") || filterCommands(q).length > 0;
        },
        items: ({ query }) => filterCommands(query),
        command: ({ editor, range, props }) => props.run(editor, range, env),
        render: popup<Command>(
          (p) => p.items.map((c) => ({ key: c.title, group: p.query.trim() ? undefined : c.group, title: c.title, icon: c.icon, hint: c.hint, pick: () => p.command(c) })),
          "No matching elements",
        ),
      }),
    ];
  },
});

interface TagHit {
  id: string;
  name: string;
  create?: boolean;
}

/** "#" → an inline tag (pick a tag or create one); the note gets the tag too. */
export const InlineTagSuggest = Extension.create<{ env: EditorEnv }>({
  name: "inlineTagSuggest",
  addOptions() {
    return { env: { noteId: "", pickFiles: () => undefined } };
  },
  addProseMirrorPlugins() {
    const env = this.options.env;
    // Tag names, for the synchronous `allow` check below; refreshed on every lookup.
    let names: string[] = [];
    void db.query<{ name: string }>("SELECT name FROM tags WHERE deleted = 0").then((r) => (names = r.map((t) => t.name.toLowerCase())));
    const find = (q: string) =>
      db.query<TagHit>(`SELECT id, name FROM tags WHERE deleted = 0 AND name LIKE ? ESCAPE '\\' ORDER BY name COLLATE NOCASE LIMIT 8`, [`%${q.replace(/[\\%_]/g, (c) => "\\" + c)}%`]);
    return [
      Suggestion<TagHit>({
        editor: this.editor,
        pluginKey: new PluginKey("inlineTag"),
        char: "#",
        // Tag names can have spaces ("nyt ARTICLE"); a spaced query is kept only while a tag still matches.
        allowSpaces: true,
        allow: ({ state, range }) => {
          const q = state.doc.textBetween(range.from + 1, range.to, " ");
          // "# " at the start of a line is a heading (markdown), not a tag. Past a space, stay open only while
          // an existing tag still matches, so "#1 priority is…" + Enter doesn't create a tag.
          if (q.startsWith(" ")) return false;
          const t = q.trim().toLowerCase();
          return !q.includes(" ") || names.some((n) => n.includes(t));
        },
        items: async ({ query }) => {
          const q = query.trim();
          void db.query<{ name: string }>("SELECT name FROM tags WHERE deleted = 0").then((r) => (names = r.map((t) => t.name.toLowerCase())));
          const rows = await find(q);
          const exact = rows.some((t) => t.name.toLowerCase() === q.toLowerCase());
          // "issue #42", "#1": numbers aren't offered as new tags, so Enter there just starts a new line.
          return q && !exact && !/^\d+$/.test(q) ? [...rows, { id: "", name: q, create: true }] : rows;
        },
        command: async ({ editor, range, props }) => {
          let { id } = props;
          if (props.create) id = await createTag(props.name);
          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              { type: "inlineTag", attrs: { id, name: props.name } },
              { type: "text", text: " " },
            ])
            .run();
          if (env.noteId) void addTagToNotes([env.noteId], id);
        },
        render: popup<TagHit>(
          (p) =>
            p.items.map((t) => ({
              key: t.id || "create",
              title: t.create ? `Create tag "${t.name}"` : t.name,
              icon: <Hash size={16} className={t.create ? "ic-create" : undefined} />,
              pick: () => p.command(t),
            })),
          "No matching tags",
        ),
      }),
    ];
  },
});

interface NoteHit {
  id: string;
  title: string;
  create?: boolean;
}

/** "@" → link to a note (or create a new linked note). */
export const NoteMention = Extension.create<{ env: EditorEnv }>({
  name: "noteMention",
  addOptions() {
    return { env: { noteId: "", pickFiles: () => undefined } };
  },
  addProseMirrorPlugins() {
    const env = this.options.env;
    return [
      Suggestion<NoteHit>({
        editor: this.editor,
        pluginKey: new PluginKey("noteMention"),
        char: "@",
        allowSpaces: true,
        // "lunch @ noon": an @ followed by a space is text.
        allow: ({ state, range }) => !state.doc.textBetween(range.from + 1, range.to, " ").startsWith(" "),
        items: async ({ query }) => {
          const q = query.trim();
          const rows = await db.query<NoteHit>(
            `SELECT id, title FROM notes WHERE deleted = 0 AND trashed_at IS NULL AND id <> ? AND title LIKE ? ESCAPE '\\' ORDER BY updated DESC LIMIT 8`,
            [env.noteId, `%${q.replace(/[\\%_]/g, (c) => "\\" + c)}%`],
          );
          // Past a space, offer only notes that still match: "meet @Gio tomorrow" + Enter is a new line,
          // not a new note called "Gio tomorrow".
          if (q.includes(" ") && !rows.length) return [];
          return q && !q.includes(" ") ? [...rows, { id: "", title: q, create: true }] : rows;
        },
        command: async ({ editor, range, props }) => {
          let { id, title } = props;
          if (props.create) id = await createNote({ title });
          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              { type: "text", text: title || "Untitled", marks: [{ type: "link", attrs: { href: `/note/${id}`, "data-note-link": id } }] },
              { type: "text", text: " " },
            ])
            .run();
        },
        render: popup<NoteHit>(
          (p) =>
            p.items.map((n) => ({
              key: n.id || "create",
              title: n.create ? `New linked note "${n.title}"` : n.title || "Untitled",
              icon: n.create ? <Plus size={16} /> : <FileText size={16} />,
              pick: () => p.command(n),
            })),
          "Type to find a note",
        ),
      }),
    ];
  },
});
